import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DT, DS2 } from '../theme/designTokens';
import { useIsMobile } from '../hooks/useBreakpoint';
import { useAuth } from '../context/AuthContext';
import { useIdentity, resolveDisplayName } from '../hooks/useIdentity';
import { PageContent } from '../components/ui/PageContent';
import { FilterPill } from '../components/ui/FilterPill';
import { AtmosphericGlow } from '../components/ui/AtmosphericGlow';
import { GradientText } from '../components/ui/GradientText';
import { Footer } from '../components/shell/Footer';
import { ASSETS } from '../config/assets';
import { apiClient } from '../network/ApiClient';
import type { MatchHistoryEntry, MatchTypeFilter } from '../types/game';
import { useFriends } from '../context/FriendContext';
import { showSocialToast } from '../components/social/SocialToastManager';
import { uiFromError, renderUiMessage } from '../i18n/uiMessage';
import { CountryFlag } from '../components/ui/CountryFlag';
import { PlayerAvatar } from '../components/ui/PlayerAvatar';
import { UnreadBadge } from '../components/ui/UnreadBadge';
import { useTranslation, Trans } from '../i18n/react';

/** Exported for unit testing — maps the Online/Bots filter pill to the backend's match_type param. */
export function filterToMatchType(filter: 'online' | 'bots'): MatchTypeFilter {
  return filter === 'bots' ? 'bots' : 'online_ranked';
}

/**
 * HOME — logged-in dashboard (Figma "HOME" frame, 2560×3774 export,
 * values ×0.75 per the established desktop scale, DS2 fluid tokens for
 * the shared heading/eyebrow/paragraph styles — same system as Landing).
 *
 * BACKEND SLOTS (deliberately empty until wired — see MATCHES/FRIENDS
 * arrays below): recent matches rows, friends rows + count, country
 * flags, avatar images. The row components render the exact Figma
 * layout the moment data arrives.
 *
 * Figma's decorative custom scrollbar graphic on the Friends panel is
 * intentionally omitted (app-wide no-visible-scrollbars rule).
 */

// ── Backend data slots ───────────────────────────────────────────────────────

/** Player Identity V1: one team member's identity fragment — split out of
 *  the old joined "Name A / Name B" string so each human name can carry
 *  its own flag. */
interface TeamMemberFragment {
  name: string;
  countryCode?: string | null;
  isBot?: boolean;
}

interface MatchRowData {
  score:    string;  // "100 - 92"
  /** Elapsed "m:ss" (or "—" when unknown); the unit label is added at render via the catalog. */
  duration: string;
  teamA:    TeamMemberFragment[];
  teamB:    TeamMemberFragment[];
}

interface FriendRowData {
  status:      'online' | 'in_game' | 'offline';
  name:        string;
  /** Raw country code from the payload — CountryFlag validates it. */
  countryCode?: string | null;
  /** Raw avatar key from the payload — PlayerAvatar validates it. */
  avatarKey?:  string | null;
  onInvite?:   () => void;
  onRemove?:   () => void;
  onMessage?:  () => void;
  /** Present only for a friend in a real (spectatable) multiplayer match —
   *  see FriendListItem.spectate_game_id. */
  onSpectate?: () => void;
  unreadCount?: number;
}

// Until the backend is wired, empty lists render this many placeholder rows
// (matching Figma's row counts) so the table structure and dividers are
// visible: static labels are real, dynamic values are dim skeleton bars.
const PLACEHOLDER_MATCH_ROWS = 3;
const PLACEHOLDER_FRIEND_ROWS = 5;
const RECENT_MATCHES_LIMIT = 3;

function formatMatchDuration(createdAt: string, finishedAt: string): string {
  const ms = new Date(finishedAt).getTime() - new Date(createdAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function matchEntryToRow(
  entry: MatchHistoryEntry,
  me: { name: string; countryCode?: string | null },
): MatchRowData {
  const myFragment: TeamMemberFragment = { name: me.name, countryCode: me.countryCode, isBot: false };
  const myTeam: TeamMemberFragment[] = entry.partner
    ? [myFragment, { name: entry.partner.display_name, countryCode: entry.partner.country_code, isBot: entry.partner.is_bot }]
    : [myFragment];
  const oppTeam: TeamMemberFragment[] = entry.opponents.map(o => ({
    name: o.display_name, countryCode: o.country_code, isBot: o.is_bot,
  }));

  return {
    score:    `${entry.team_scores.team_0} - ${entry.team_scores.team_1}`,
    duration: formatMatchDuration(entry.created_at, entry.finished_at),
    teamA:    entry.my_team === 0 ? myTeam : oppTeam,
    teamB:    entry.my_team === 1 ? myTeam : oppTeam,
  };
}

/** Renders a team's players as [flag] Name fragments joined by " / " —
 *  each human name carries its own flag; bots never do. */
function TeamMembers({ members }: { members: TeamMemberFragment[] }) {
  if (members.length === 0) return <>—</>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 6px' }}>
      {members.map((m, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {i > 0 && <span aria-hidden="true" style={{ opacity: 0.6 }}>/</span>}
          {!m.isBot && <CountryFlag countryCode={m.countryCode} width={14} />}
          <span>{m.name}</span>
        </span>
      ))}
    </span>
  );
}

/** Dim skeleton bar marking a backend-data slot. */
function Bar({ w }: { w: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display:      'inline-block',
        width:        w,
        height:       14,
        borderRadius: 7,
        background:   'rgba(245,245,245,0.12)',
        verticalAlign: 'middle',
      }}
    />
  );
}

// ── Shared section header (identical pattern to Landing) ─────────────────────

function SectionHeader({ eyebrow, heading, sub, compact }: { eyebrow: string; heading: ReactNode; sub: string; compact?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: compact ? 13 : `clamp(1.1rem, ${DS2.type.eyebrow.pref}vw, ${DS2.type.eyebrow.max}px)`, fontWeight: DS2.type.eyebrow.weight, fontFamily: DS2.font.body, color: DS2.color.gold, letterSpacing: '0.158em', textTransform: 'uppercase' }}>
        {eyebrow}
      </div>
      <h2
        style={{
          fontSize:      compact ? 22 : `clamp(1.6rem, ${DS2.type.sectionHeading.pref}vw, ${DS2.type.sectionHeading.max}px)`,
          lineHeight:    compact ? '26px' : DS2.type.sectionHeading.lineHeight,
          letterSpacing: '-0.02em',
          fontWeight:    700,
          fontFamily:    DS2.font.heading,
          color:         DT.color.textPrimary,
          margin:        compact ? '10px 0 0' : '11px 0 0',
        }}
      >
        {heading}
      </h2>
      <p
        style={{
          fontSize:   compact ? 13 : `clamp(0.9rem, ${DS2.type.paragraph.pref}vw, ${DS2.type.paragraph.max}px)`,
          lineHeight: compact ? '19px' : DS2.type.paragraph.lineHeight,
          fontFamily: DS2.font.body,
          fontWeight: 300,
          color:      DS2.color.textBright,
          margin:     compact ? '6px 0 0' : '9px 0 0',
        }}
      >
        {sub}
      </p>
    </div>
  );
}

// ── Hero action cards (Figma 477×101 ×0.75 → 357.75×75.75) ──────────────────

const ACTION_CARD_BASE: CSSProperties = {
  boxSizing:      'border-box',
  display:        'flex',
  flexDirection:  'column',
  justifyContent: 'center',
  gap:            3,
  width:          '100%',
  maxWidth:       333,
  minHeight:      60,
  padding:        '10px 17px',
  textDecoration: 'none',
};

const ACTION_CARD_BASE_COMPACT: CSSProperties = {
  ...ACTION_CARD_BASE,
  gap:       1,
  minHeight: 42,
  padding:   '7px 13px',
};

function ActionCardGhost({ to, title, sub, compact }: { to: string; title: string; sub: string; compact?: boolean }) {
  return (
    <Link
      to={to}
      style={{
        ...(compact ? ACTION_CARD_BASE_COMPACT : ACTION_CARD_BASE),
        background:   '#131313',
        border:       `0.75px solid ${DS2.color.gold}`,
        borderRadius: 12,
      }}
    >
      <span style={{ fontFamily: DS2.font.body, fontWeight: 600, fontSize: compact ? 12.5 : 16.5, lineHeight: compact ? '15px' : '20px', color: DS2.color.gold }}>{title}</span>
      <span style={{ fontFamily: DS2.font.body, fontWeight: 400, fontSize: compact ? 10.5 : 13.8, lineHeight: compact ? '13px' : '18px', color: DT.color.textPrimary }}>{sub}</span>
    </Link>
  );
}

function ActionCardGold({ to, title, sub, compact }: { to: string; title: string; sub: string; compact?: boolean }) {
  return (
    <Link
      to={to}
      style={{
        ...(compact ? ACTION_CARD_BASE_COMPACT : ACTION_CARD_BASE),
        background:   DS2.gradient.gold,
        border:       '0.75px solid #C9A84C',
        boxShadow:    '0px 18px 48px -18px rgba(201, 168, 76, 0.45)',
        borderRadius: 11.25,
      }}
    >
      <span style={{ fontFamily: DS2.font.body, fontWeight: 600, fontSize: compact ? 12.5 : 16.5, lineHeight: compact ? '15px' : '20px', color: '#0B0B0B' }}>{title}</span>
      <span style={{ fontFamily: DS2.font.body, fontWeight: 400, fontSize: compact ? 10.5 : 13.8, lineHeight: compact ? '13px' : '18px', color: '#0B0B0B' }}>{sub}</span>
    </Link>
  );
}

// Quick links (Friends / Stats) — 36px gold outline glyphs at 0.6 opacity per Figma.
function QuickLink({ to, title, sub, icon, compact, tutorialTarget }: { to: string; title: string; sub: string; icon: ReactNode; compact?: boolean; tutorialTarget?: string }) {
  return (
    <Link to={to} data-tutorial-target={tutorialTarget} style={{ display: 'flex', gap: compact ? 10 : 11, textDecoration: 'none', alignItems: 'flex-start' }}>
      <span style={{ opacity: 0.6, flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: compact ? 2 : 3.5 }}>
        <span style={{ fontFamily: DS2.font.body, fontWeight: 600, fontSize: compact ? 13 : 16.5, lineHeight: compact ? '16px' : '20px', color: DT.color.textPrimary }}>{title}</span>
        <span style={{ fontFamily: DS2.font.body, fontWeight: 400, fontSize: compact ? 11 : 13.8, lineHeight: compact ? '14px' : '18px', color: '#A89F86' }}>{sub}</span>
      </span>
    </Link>
  );
}

// Generic UI glyphs (users / chart) — simple line icons, same precedent as
// TopNav's hand-drawn Hamburger/Person SVGs. Gold stroke per Figma.
function UsersSVG({ size = 27 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="9" cy="8" r="3.2" stroke="#C9A84C" strokeWidth="1.75" />
      <path d="M3.5 19c0-2.9 2.46-5 5.5-5s5.5 2.1 5.5 5" stroke="#C9A84C" strokeWidth="1.75" strokeLinecap="round" />
      <path d="M15.5 5.6a3.2 3.2 0 1 1 1.6 6" stroke="#C9A84C" strokeWidth="1.75" strokeLinecap="round" />
      <path d="M17.6 14.3c1.9.55 3.4 2 3.4 4.2" stroke="#C9A84C" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function ChartSVG({ size = 27 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M4 20V10M10 20V4M16 20v-8M21 20H3" stroke="#C9A84C" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function TrophySVG({ size = 27 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M7 4h10v4a5 5 0 0 1-10 0V4Z" stroke="#C9A84C" strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M7 5H4v1.5A3.5 3.5 0 0 0 7.5 10" stroke="#C9A84C" strokeWidth="1.75" strokeLinecap="round" />
      <path d="M17 5h3v1.5A3.5 3.5 0 0 1 16.5 10" stroke="#C9A84C" strokeWidth="1.75" strokeLinecap="round" />
      <path d="M12 13v3M9 20h6M9.5 20c0-2 .8-3 2.5-3s2.5 1 2.5 3" stroke="#C9A84C" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function GiftSVG() {
  return (
    <svg width={19} height={17} viewBox="0 0 24 22" fill="none" aria-hidden>
      <rect x="2" y="7" width="20" height="4" stroke="#C9A84C" strokeWidth="1.75" />
      <rect x="4" y="11" width="16" height="9" stroke="#C9A84C" strokeWidth="1.75" />
      <path d="M12 7v13M12 7C10 3 6 2 6 4.5S10 7 12 7Zm0 0c2-4 6-5 6-2.5S14 7 12 7Z" stroke="#C9A84C" strokeWidth="1.75" />
    </svg>
  );
}

function TrashSVG() {
  return (
    <svg width={19} height={17} viewBox="0 0 24 22" fill="none" aria-hidden>
      <path d="M3 5h18M8 5V3h8v2M6 5l1.2 15h9.6L18 5" stroke="#F24949" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

// ── Recent Matches (table row + divider, Figma ×0.75) ───────────────────────

const MATCH_CELL: CSSProperties = {
  fontFamily: DS2.font.body,
  fontWeight: 500,
  fontSize:   17,
  lineHeight: '31px',
  color:      '#FFFFFF',
};

const TEAM_LABEL: CSSProperties = {
  fontFamily: DS2.font.body,
  fontWeight: 700,
  fontSize:   11.6,
  lineHeight: '31px',
};

const ROW_DIVIDER: CSSProperties = { borderTop: '2.25px solid rgba(245,245,245,0.2)' };

function MatchRow({ row, compact }: { row?: MatchRowData; compact?: boolean }) {
  const { t } = useTranslation('home');
  const cell: CSSProperties = compact ? { ...MATCH_CELL, fontSize: 14, lineHeight: '26px' } : MATCH_CELL;
  const label: CSSProperties = compact ? { ...TEAM_LABEL, fontSize: 10, lineHeight: '26px' } : TEAM_LABEL;
  const durationText = row ? (row.duration === '—' ? '—' : t('matches.duration', { time: row.duration })) : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 14 : 25, padding: '9px 0', flexWrap: 'wrap' }}>
      <span style={{ ...cell, minWidth: compact ? 56 : 68 }}>{row ? row.score : <Bar w={compact ? 56 : 68} />}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: compact ? 9 : 13 }}>
        {/* gold duration dot — Figma 24.22px gradient circle */}
        <span style={{ width: compact ? 12 : 15, height: compact ? 12 : 15, borderRadius: 11.25, background: DS2.gradient.gold, border: '0.5px solid #C9A84C', flexShrink: 0 }} />
        <span style={cell}>{row ? durationText : <Bar w={compact ? 64 : 82} />}</span>
      </span>
      <span style={{ ...label, color: '#CD2027' }}>{t('matches.teamA')}</span>
      <span style={cell}>{row ? <TeamMembers members={row.teamA} /> : <Bar w={compact ? 150 : 193} />}</span>
      <span style={{ ...label, color: '#2C71B8' }}>{t('matches.teamB')}</span>
      <span style={cell}>{row ? <TeamMembers members={row.teamB} /> : <Bar w={compact ? 110 : 143} />}</span>
    </div>
  );
}

// Filter pills — promoted to the shared DS2 component (see ui/FilterPill.tsx),
// reused by the Friends screen's tab switcher.

// ── Friends panel row ────────────────────────────────────────────────────────

const FRIEND_ACTION: CSSProperties = {
  fontFamily: DS2.font.body,
  fontWeight: 300,
  fontSize:   13.7,
  lineHeight: '28px',
  color:      '#FFFFFF',
  background: 'transparent',
  border:     'none',
  cursor:     'pointer',
  padding:    0,
  WebkitTapHighlightColor: 'transparent',
};

const FRIEND_STATUS_COLOR: Record<FriendRowData['status'], string> = {
  online:   '#22FB04',
  in_game:  '#A855F7',
  offline:  '#F24949',
};

// Semantic presence enum → catalog key, resolved at render (never at import).
const FRIEND_STATUS_KEY = {
  online:   'friends.statusOnline',
  in_game:  'friends.statusInGame',
  offline:  'friends.statusOffline',
} as const;

function FriendRow({ row, compact }: { row?: FriendRowData; compact?: boolean }) {
  const { t } = useTranslation('home');
  const cell: CSSProperties = compact ? { ...MATCH_CELL, fontSize: 14, lineHeight: '26px' } : MATCH_CELL;
  const action: CSSProperties = compact ? { ...FRIEND_ACTION, fontSize: 12.5, lineHeight: '26px' } : FRIEND_ACTION;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 14 : 24, padding: '9px 0', flexWrap: 'wrap' }}>
      <span style={{ ...cell, color: row ? FRIEND_STATUS_COLOR[row.status] : undefined, minWidth: compact ? 48 : 56 }}>
        {row ? t(FRIEND_STATUS_KEY[row.status]) : <Bar w={compact ? 48 : 56} />}
      </span>
      {/* Player Identity V1: flag + avatar + full name (Figma 59×59 flag slot). */}
      <span style={{ display: 'flex', alignItems: 'center', gap: compact ? 8 : 9.3, minWidth: compact ? 100 : 113 }}>
        {row && <CountryFlag countryCode={row.countryCode} width={compact ? 18 : 21} decorative />}
        {row && <PlayerAvatar avatarKey={row.avatarKey} name={row.name} size={compact ? 22 : 25} />}
        <span title={row ? row.name : undefined} style={cell}>{row ? row.name : <Bar w={compact ? 100 : 113} />}</span>
      </span>
      <span style={{ display: 'flex', gap: compact ? 18 : 29, marginLeft: compact ? 0 : 'auto' }}>
        <button type="button" style={action} onClick={row?.onInvite} disabled={!row?.onInvite}>{t('friends.invite')}</button>
        {/* Only rendered when this friend is actually spectatable — no
            button at all otherwise, not just a disabled one. */}
        {row?.onSpectate && (
          <button type="button" style={action} onClick={row.onSpectate}>👀 {t('friends.spectate')}</button>
        )}
        <button type="button" style={action} onClick={row?.onMessage} disabled={!row?.onMessage}>
          {t('friends.message')}
          {row && row.unreadCount ? <UnreadBadge count={row.unreadCount} /> : null}
        </button>
        <button type="button" style={{ ...action, opacity: 0.4, cursor: 'default' }} disabled>{t('friends.profile')}</button>
      </span>
      <span style={{ display: 'flex', gap: compact ? 18 : 30 }}>
        <button type="button" style={{ ...action, fontWeight: 600, color: '#C9A84C', display: 'flex', alignItems: 'center', gap: 6, opacity: 0.4, cursor: 'default' }} disabled>
          <GiftSVG /> {t('friends.gift')}
        </button>
        <button type="button" style={{ ...action, color: '#F24949', display: 'flex', alignItems: 'center', gap: 9 }} onClick={row?.onRemove} disabled={!row?.onRemove}>
          <TrashSVG /> {t('friends.remove')}
        </button>
      </span>
    </div>
  );
}

/**
 * Styled username block (flag + gradient) used as a Trans component
 * placeholder: the catalog owns the WHOLE greeting sentence per locale
 * ("Welcome back, <styledName>{{name}}</styledName>" / "¡Hola de nuevo,
 * <styledName>{{name}}</styledName>!") and may reposition the name freely.
 * The interpolated name arrives as children — plain React text, so
 * escaping/hostile-name safety is React's, with no HTML in the catalog.
 * Module-level (stable component identity) so re-renders update it in
 * place instead of remounting the flag + name subtree.
 */
function StyledName({ countryCode, compact, children }: { countryCode?: string | null; compact?: boolean; children?: ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, verticalAlign: 'middle' }}>
      <CountryFlag countryCode={countryCode} width={compact ? 20 : 25} loading="eager" />
      <GradientText>{children}</GradientText>
    </span>
  );
}

// ── Screen ──────────────────────────────────────────────────────────────────

export function HomeDS2Screen() {
  const isMobile = useIsMobile();
  const { t } = useTranslation('home');
  const { user, devDisplayName } = useAuth();
  const identity = useIdentity();
  // Username-first: backend display_name → Firebase displayName → email prefix
  const displayName = resolveDisplayName(identity, user, devDisplayName || 'Player');
  const [matchFilter, setMatchFilter] = useState<'online' | 'bots'>('online');

  const [matchHistory, setMatchHistory]       = useState<MatchHistoryEntry[]>([]);
  const [historyLoaded, setHistoryLoaded]     = useState(false);

  useEffect(() => {
    // Guards against a rapid Online/Bots toggle where an earlier, slower
    // request resolves after a later one and overwrites fresher data.
    let stale = false;
    setHistoryLoaded(false);
    apiClient.getMatchHistory(filterToMatchType(matchFilter))
      .then(r => { if (!stale) setMatchHistory(r.history); })
      .catch(() => { if (!stale) setMatchHistory([]); })
      .finally(() => { if (!stale) setHistoryLoaded(true); });
    return () => { stale = true; };
  }, [matchFilter]);

  const recentMatches = matchHistory
    .slice(0, RECENT_MATCHES_LIMIT)
    .map(entry => matchEntryToRow(entry, { name: displayName, countryCode: identity?.countryCode }));

  const { friends, friendsLoading, removeFriend, conversations } = useFriends();
  const navigate = useNavigate();

  async function handleInvite(friendUserId: number) {
    try {
      const sessions = await apiClient.getSessions();
      const sessionId = sessions.my_active_session_id ?? (await apiClient.createSession()).session_id;
      await apiClient.sendInvitation(friendUserId, sessionId);
      navigate(`/lobby/${sessionId}`);
    } catch (e) {
      // Phase 12 (C3a): coded errors localize via the catalog; uncoded
      // backend prose stays verbatim; the generic case is ours.
      showSocialToast(renderUiMessage(t, uiFromError(e, 'friends.inviteError')));
    }
  }

  const friendRows: FriendRowData[] = friends.map(f => ({
    status:      f.status,
    name:        f.friend.display_name,
    countryCode: f.friend.country_code ?? null,
    avatarKey:   f.friend.avatar_key ?? null,
    onInvite:    () => handleInvite(f.friend.id),
    onRemove:    () => removeFriend(f.friendship_id),
    onMessage:   () => navigate(`/messages/${f.friend.id}`),
    onSpectate:  f.spectate_game_id != null ? () => navigate(`/spectate/${f.spectate_game_id}`) : undefined,
    unreadCount: conversations.find(c => c.friend.id === f.friend.id)?.unread_count ?? 0,
  }));

  const heroImage = (
    <div style={{ width: isMobile ? 140 : 355, maxWidth: isMobile ? 140 : 355, position: 'relative', flexShrink: 0 }}>
      <img
        src={ASSETS.marketing.HERO_TDG}
        alt=""
        style={{
          display:      'block',
          width:        '100%',
          height:       'auto',
          aspectRatio:  '1 / 1',
          objectFit:    'cover',
          borderRadius: isMobile ? 16 : `clamp(16px, ${DS2.spacing.heroImageRadius.pref}vw, ${DS2.spacing.heroImageRadius.max}px)`,
          filter:       'drop-shadow(0 24px 48px rgba(0,0,0,0.60))',
        }}
        draggable={false}
      />
    </div>
  );

  const heroHeading = (
    <h1
      style={{
        fontSize:      isMobile ? 26 : `clamp(2rem, ${DS2.type.heroHeading.pref}vw, ${DS2.type.heroHeading.max}px)`,
        lineHeight:    1.2,
        letterSpacing: '-0.02em',
        fontWeight:    700,
        fontFamily:    DS2.font.heading,
        color:         DT.color.textPrimary,
        margin:        0,
        overflowWrap:  'anywhere',
      }}
    >
      <Trans
        t={t}
        i18nKey="hero.welcome"
        values={{ name: displayName }}
        components={{ styledName: <StyledName countryCode={identity?.countryCode} compact={isMobile} /> }}
      />
    </h1>
  );

  const heroActions = (
    <>
      <div style={{ marginTop: isMobile ? 16 : 22, fontFamily: DS2.font.heading, fontWeight: 700, fontSize: isMobile ? 12 : 14.7, letterSpacing: '1.66px', textTransform: 'uppercase', color: DS2.color.gold }}>
        {t('hero.playEyebrow')}
      </div>

      <div data-tutorial-target="home-dashboard" style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 10 : 11.2, marginTop: 11 }}>
        <ActionCardGhost to="/play/ai" title={t('hero.playBotsTitle')} sub={t('hero.playBotsSub')} compact={isMobile} />
        <ActionCardGold to="/play/online" title={t('hero.playOnlineTitle')} sub={t('hero.playOnlineSub')} compact={isMobile} />
      </div>

      <div style={{ display: 'flex', gap: isMobile ? 32 : 48, marginTop: isMobile ? 20 : 25, flexWrap: 'wrap' }}>
        <QuickLink to="/friends" title={t('hero.friendsTitle')} sub={t('hero.friendsSub')} icon={<UsersSVG size={isMobile ? 20 : 24} />} compact={isMobile} />
        <QuickLink to="/stats" title={t('hero.statsTitle')} sub={t('hero.statsSub')} icon={<ChartSVG size={isMobile ? 20 : 24} />} compact={isMobile} tutorialTarget="home-stats" />
        <QuickLink to="/achievements" title={t('hero.achievementsTitle')} sub={t('hero.achievementsSub')} icon={<TrophySVG size={isMobile ? 20 : 24} />} compact={isMobile} />
      </div>
    </>
  );

  return (
    // flex column fills the available viewport height so Footer's
    // marginTop:'auto' sticks flush to the bottom on short pages (see
    // AppShell.tsx's content-wrapper comment).
    <div style={{ width: '100%', background: DT.color.pageBg, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

      {/* ── HERO: welcome + actions ─────────────────────────────────── */}
      <div style={{ position: 'relative', paddingTop: isMobile ? 20 : 37, paddingBottom: isMobile ? 20 : 37 }}>
        <AtmosphericGlow width={825} height={525} top={198} leftOffset={-142} />
        <PageContent maxWidth={DS2.layout.contentMaxWidth}>
          {isMobile ? (
            <div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                {heroImage}
                <div style={{ flex: '1 1 0', minWidth: 0 }}>{heroHeading}</div>
              </div>
              {heroActions}
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ display: 'flex', gap: 100, alignItems: 'center', flexWrap: 'nowrap' }}>
                {heroImage}
                <div style={{ flex: '0 1 480px', minWidth: 0 }}>
                  {heroHeading}
                  {heroActions}
                </div>
              </div>
            </div>
          )}
        </PageContent>
      </div>

      {/* ── RECENT MATCHES ──────────────────────────────────────────── */}
      <div style={{ position: 'relative', paddingTop: isMobile ? 28 : 53, paddingBottom: isMobile ? 28 : 53 }}>
        <AtmosphericGlow width={825} height={639} top={-54} leftOffset={-796} />
        <PageContent maxWidth={DS2.layout.contentMaxWidth}>
          <SectionHeader
            eyebrow={t('matches.eyebrow')}
            heading={t('matches.heading')}
            sub={t('matches.sub')}
            compact={isMobile}
          />

          {/* Filter row — VS: + Online/Bots pills */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 18.7, marginTop: isMobile ? 20 : 29.3 }}>
            <span style={isMobile ? { ...MATCH_CELL, fontSize: 14, lineHeight: '26px' } : MATCH_CELL}>{t('matches.vs')}</span>
            <FilterPill label={t('matches.filterOnline')} active={matchFilter === 'online'} onClick={() => setMatchFilter('online')} compact={isMobile} />
            <FilterPill label={t('matches.filterBots')} active={matchFilter === 'bots'} onClick={() => setMatchFilter('bots')} compact={isMobile} />
          </div>

          {/* Figma puts a divider BELOW each match row (Vectors 8-10). */}
          <div style={{ marginTop: isMobile ? 16 : 21.3 }}>
            {!historyLoaded ? (
              Array.from({ length: PLACEHOLDER_MATCH_ROWS }, (_, i) => (
                <div key={i} style={{ borderBottom: '2.25px solid rgba(245,245,245,0.2)' }}>
                  <MatchRow row={undefined} compact={isMobile} />
                </div>
              ))
            ) : recentMatches.length ? (
              recentMatches.map((row, i) => (
                <div key={i} style={{ borderBottom: '2.25px solid rgba(245,245,245,0.2)' }}>
                  <MatchRow row={row} compact={isMobile} />
                </div>
              ))
            ) : (
              <div style={{ ...MATCH_CELL, color: '#A89F86', padding: '10px 0' }}>
                {matchFilter === 'bots' ? t('matches.emptyBots') : t('matches.emptyOnline')}
              </div>
            )}
          </div>
        </PageContent>
      </div>

      {/* ── FRIENDS PANEL — Figma Rectangle 14: #131313 band, 0.12 glow ── */}
      <div style={{ position: 'relative', background: '#131313', paddingTop: isMobile ? 28 : 53, paddingBottom: isMobile ? 28 : 53 }}>
        <AtmosphericGlow width={1126.5} height={270} top={0} leftOffset={-16} opacity={0.12} />
        <PageContent maxWidth={DS2.layout.contentMaxWidth}>
          <SectionHeader
            eyebrow={t('friends.eyebrow')}
            heading={friendsLoading ? t('friends.heading') : t('friends.headingCount', { count: friendRows.length })}
            sub={t('friends.sub')}
            compact={isMobile}
          />

          {/* Figma puts dividers BETWEEN friend rows (Vectors 11-14). */}
          <div style={{ marginTop: isMobile ? 20 : 29.3 }}>
            {friendsLoading ? (
              Array.from({ length: PLACEHOLDER_FRIEND_ROWS }, (_, i) => (
                <div key={i} style={i > 0 ? ROW_DIVIDER : undefined}>
                  <FriendRow row={undefined} compact={isMobile} />
                </div>
              ))
            ) : friendRows.length ? (
              friendRows.map((row, i) => (
                <div key={i} style={i > 0 ? ROW_DIVIDER : undefined}>
                  <FriendRow row={row} compact={isMobile} />
                </div>
              ))
            ) : (
              <div style={{ ...MATCH_CELL, color: '#A89F86', padding: '10px 0' }}>
                {t('friends.empty')}
              </div>
            )}
          </div>
        </PageContent>
      </div>

      <Footer />
    </div>
  );
}
